import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// components
import { Paper, Button, FormLabel, FormControl, TextField, Tooltip, IconButton } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

// reducers
import { getSelectedFlowPermissions } from "../../../../reducers/flowItems";
import {
    getFlowExternalServiceForSelectedFlowItem,
    getFlowItemExternalServiceDefeaultFields,
} from "../../../../reducers/flowExternalServices";

// actions
import {
    updateAttribute,
    setHasUnsavedLayoutChanges,
    invalidateItemAndChildren,
} from "../../../../actions/flowActions";
import { changeExternalServiceFieldsUsed, setServiceSelectedLayout } from "../../../../actions/flowControlActions";

// types
import { IAppState } from "../../../../types/stores";
import {
    FlowExternalService,
    FlowExternalServiceParameter,
    FlowPermissions,
    FlowExternalServiceHardcode,
} from "../../../../types/stores/flowTypes";
import LayoutPreview from "../../../layouts-external-service/LayoutPreview";
import LayoutBuilder, { LayoutTypes } from "../../../layouts-external-service/LayoutBuilder";

// helpers
import { labelCompare } from "../../../../helpers/selectHelper";
import { getFlowExternalServiceParametersForSelectedFlowItem } from "../../../../reducers/flowExternalServiceParameters";
import { getFlowExternalServiceHardcodesForSelectedFlowItem } from "../../../../reducers/flowExternalServiceHardcodes";
import { IExternalServiceParameter } from "../../../../types/stores/flowControlData";
import {
    IExternalServiceLayout,
    IExternalServiceLayoutField,
} from "../../../../types/stores/externalServiceLayoutData";
import { request } from "../../../../helpers/httpInterceptor";
import { IEnrichmentField } from "../../../../types/stores/umbrellaData";
import { getServiceFieldData, getItemParameterValue } from "../../../../helpers/flowControlHelper";

interface IProps {
    layoutType: number;
    isRequired?: boolean;
    isTable?: boolean;
    hasColumnOptFeats?: boolean;
    showColumnOptService?: boolean;
}

const FlowControlLayoutPicker: React.FC<IProps> = ({
    layoutType,
    isRequired,
    isTable,
    hasColumnOptFeats,
    showColumnOptService,
}: IProps) => {
    const propertyName = layoutType == LayoutTypes.Input ? "InputLayoutId" : "OutputLayoutId";

    const dispatch = useDispatch();
    const [selectedLayout, setSelectedLayout] = useState(0);
    const [editLayoutMode, setEditLayoutMode] = useState(false);
    const [exportLockedOut, setExportLockedOut] = useState(false);
    const [layouts, setLayouts] = useState<Array<IExternalServiceLayout>>([]);
    const [layout, setLayout] = useState<IExternalServiceLayout | null>(null);

    const flowExternalService = useSelector<IAppState, FlowExternalService>(state =>
        getFlowExternalServiceForSelectedFlowItem(state)
    );
    const flowPermissions = useSelector<IAppState, FlowPermissions>(state => getSelectedFlowPermissions(state));
    const awsIsDownloading = useSelector<IAppState, boolean>(state => state.aws.isDownloading);
    const allLayouts = useSelector((state: IAppState) => state.externalServiceLayoutData.externalServiceLayouts);

    const externalServiceParameters = useSelector<IAppState, Array<IExternalServiceParameter>>(
        state => state.flowControlData.externalServiceParameters
    );

    const flowExternalServiceParameters = useSelector<IAppState, Array<FlowExternalServiceParameter>>(state =>
        getFlowExternalServiceParametersForSelectedFlowItem(state)
    );
    const flowExternalServiceHardcodes = useSelector<IAppState, Array<FlowExternalServiceHardcode>>(state =>
        getFlowExternalServiceHardcodesForSelectedFlowItem(state)
    );
    const itemServiceDefaultFields = useSelector((state: IAppState) => getFlowItemExternalServiceDefeaultFields(state));
    const listExternalParameters = useSelector((state: IAppState) => state.flowControlData.externalParameters);

    //getting Internal Matchback id
    const idInternalMatchback = listExternalParameters.find(
        parameter => parameter.ParameterName == "matchback_type"
    )?.ParameterId;

    //getting Internal Table Name Length id
    const idTableNameLength = listExternalParameters.find(
        parameter => parameter.ParameterName == "tableCodeOverride"
    )?.ParameterId;

    //getting Internal Matchback chechbox value
    const internalMatchback = flowExternalServiceParameters.filter(
        parameter => parameter.ParameterId == idInternalMatchback
    )[0];

    //getting flow extenal service parameters for Table Name Length
    const flowExternalServiceParameterTableName = flowExternalServiceParameters.filter(
        parameter => parameter.ParameterId == idTableNameLength
    );

    //getting Field Name Type value
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);
    const altUEFieldName =
        enabledFeatures.includes("flow-control-field-name-type") &&
        getItemParameterValue(listExternalParameters, flowExternalServiceParameters, "output_field_names") == "true";

    //getting selected enrichmentFields
    const selectedFieldsUE = useSelector<IAppState, Array<IEnrichmentField>>(
        state => state.umbrellaData.selectedFieldsItem || []
    );

    // all fields
    const allLayoutFields = useSelector(
        (state: IAppState) => state.externalServiceLayoutData.externalServiceLayoutFields
    );
    const standardFields = useSelector((state: IAppState) => state.externalServiceLayoutData.externalServiceFields);
    const externalServices = useSelector((state: IAppState) => state.flowControlData.externalServices);

    //getting Internal Matchback value
    const tableNameLength = flowExternalServiceParameterTableName[0];

    useEffect(() => {
        if (isTable) {
            setLayouts(allLayouts.filter(x => x.RelativePositions && x.Delimiter == ","));
        } else {
            setLayouts(allLayouts);
        }
    }, [isTable, allLayouts]);

    useEffect(() => {
        if (flowExternalService && flowExternalService[propertyName] > 0) {
            setSelectedLayout(flowExternalService[propertyName]);
        } else {
            setSelectedLayout(0);
        }
        setLayout(layouts.find(l => l.LayoutId == flowExternalService[propertyName]) || null);
    }, [dispatch, flowExternalService, propertyName, layouts]);

    useEffect(() => {
        setEditLayoutMode(false);
    }, [dispatch, layout]);

    const handleLayoutChanged = (_event, value: number | null) => {
        dispatch(
            updateAttribute("flowExternalServices", flowExternalService.FlowExternalServiceId, propertyName, value)
        );

        if (layoutType == LayoutTypes.Input) {
            dispatch(changeExternalServiceFieldsUsed(value));
        }

        dispatch(invalidateItemAndChildren(flowExternalService.FlowItemId));
    };

    const currFields = [...allLayoutFields.filter(x => x.LayoutId == flowExternalService.InputLayoutId)];

    const getStandardField = fieldId => {
        const field = standardFields.find(x => x.ServiceFieldId == fieldId);
        if (field) {
            return field.FieldName;
        } else {
            return "Not Found";
        }
    };

    const inputLayoutSequenceField = (): IExternalServiceLayoutField => {
        const standardSeq = standardFields.find(x => x.FieldName == "sequence");
        let layoutSeq;
        if (standardSeq) {
            layoutSeq = currFields.find(x => x.ServiceFieldId == standardSeq?.ServiceFieldId);
        }
        if (!layoutSeq) {
            layoutSeq = currFields.find(x => x.FieldName == "sequence");
        }
        return layoutSeq || null;
    };

    const exportLayout = () => {
        setExportLockedOut(true);
        if (layout) {
            const excelLink = `/FlowControl/ExportLayout?LayoutId=${layout?.LayoutId}&alternateUENames=${altUEFieldName}&clientCode=${flowExternalService.WFClientCode}`;
            window.location.href = excelLink;
        } else {
            // Get the Service specific Field Data
            const serviceFields = getServiceFieldData(
                externalServiceParameters,
                flowExternalServiceParameters,
                selectedFieldsUE,
                itemServiceDefaultFields,
                externalServices,
                tableNameLength?.Value
            );

            let defaultFieldsExport: Array<IExternalServiceLayoutField> = [];
            let position = 1;
            let prevLength = 0;
            const inputLayout = layouts.find(x => x.LayoutId == flowExternalService.InputLayoutId);
            const delimited = inputLayout ? inputLayout.RelativePositions : false;

            if (internalMatchback?.Value == "true") {
                currFields.forEach(item => {
                    defaultFieldsExport.push({
                        id: item.id,
                        LayoutFieldId: item.LayoutFieldId,
                        LayoutId: item.LayoutId,
                        ServiceFieldId: item.ServiceFieldId,
                        FieldName: item.ServiceFieldId > 0 ? getStandardField(item.ServiceFieldId) : item.FieldName,
                        ShortName:
                            item.ServiceFieldId > 0
                                ? getStandardField(item.ServiceFieldId)
                                : item.FieldName != null
                                ? item.FieldName.toString().substring(0, 19)
                                : "",
                        FieldPosition: delimited ? position++ : (position += prevLength),
                        FieldLength: item.FieldLength,
                        EncryptionType: item.EncryptionType,
                        IsHardcode: false,
                        FieldAlias: null,
                        AlternateName: null,
                    });
                    prevLength = item.FieldLength;
                });
            }
            serviceFields.forEach(item => {
                if (item.FieldName == "sequence") {
                    // Skip default sequence
                    if (internalMatchback?.Value == "true") {
                        return;
                    } else {
                        const inputSeq = inputLayoutSequenceField();
                        if (inputSeq) {
                            // Use sequence from the Input Layout
                            defaultFieldsExport.push({
                                id: inputSeq.id,
                                LayoutFieldId: inputSeq.LayoutFieldId,
                                LayoutId: inputSeq.LayoutId,
                                ServiceFieldId: inputSeq.ServiceFieldId,
                                FieldName:
                                    inputSeq.ServiceFieldId > 0
                                        ? getStandardField(inputSeq.ServiceFieldId)
                                        : inputSeq.FieldName,
                                ShortName:
                                    inputSeq.ServiceFieldId > 0
                                        ? getStandardField(inputSeq.ServiceFieldId)
                                        : inputSeq.FieldName != null
                                        ? inputSeq.FieldName.toString().substring(0, 19)
                                        : "",
                                FieldPosition: delimited ? position++ : (position += prevLength),
                                FieldLength: inputSeq.FieldLength,
                                EncryptionType: inputSeq.EncryptionType,
                                IsHardcode: false,
                                FieldAlias: null,
                                AlternateName: null,
                            });
                            prevLength = inputSeq.FieldLength;
                        } else {
                            // Use sequence from default fields
                            defaultFieldsExport.push({
                                id: item.FieldId,
                                LayoutFieldId: 0,
                                LayoutId: 0,
                                ServiceFieldId: 0,
                                FieldName: item.FieldId == 0 ? item.FieldName : item.FieldId.toString(),
                                ShortName: item.FieldName.toString().substring(0, 19),
                                FieldPosition: delimited ? position++ : (position += prevLength),
                                FieldLength: item.Length,
                                EncryptionType: "NONE",
                                IsHardcode: false,
                                FieldAlias: null,
                                AlternateName: null,
                            });
                            prevLength = item.Length;
                        }
                    }
                } else {
                    const name =
                        altUEFieldName && item.AlternateName && item.AlternateName?.length > 0
                            ? item.AlternateName
                            : isNaN(parseInt(item.FieldName))
                            ? item.FieldId == 0
                                ? item.FieldName
                                : item.FieldId.toString()
                            : item.FieldName;
                    const shortName =
                        altUEFieldName && item.AlternateName && item.AlternateName?.length > 0
                            ? item.FieldName
                            : isNaN(parseInt(item.FieldName))
                            ? item.FieldName.toString().substring(0, 19)
                            : item.ShortDescription.toString().substring(0, 19);
                    defaultFieldsExport.push({
                        id: item.FieldId,
                        LayoutFieldId: 0,
                        LayoutId: 0,
                        ServiceFieldId: 0,
                        FieldName: name,
                        ShortName: shortName,
                        FieldPosition: delimited ? position++ : (position += prevLength),
                        FieldLength: item.Length,
                        EncryptionType: "NONE",
                        IsHardcode: false,
                        FieldAlias: null,
                        AlternateName: null,
                    });
                    prevLength = item.Length;
                }
            });
            flowExternalServiceHardcodes.forEach(value => {
                defaultFieldsExport.push({
                    id: value.HardcodeId,
                    LayoutFieldId: 0,
                    LayoutId: 0,
                    ServiceFieldId: 0,
                    FieldName: value.HardcodeName,
                    ShortName: value.HardcodeName.substring(0, 19),
                    FieldPosition: delimited ? position++ : (position += prevLength),
                    FieldLength: value.HardcodeValue.length,
                    EncryptionType: "NONE",
                    IsHardcode: true,
                    FieldAlias: null,
                    AlternateName: null,
                });
                prevLength = value.HardcodeValue.length;
            });

            // Fixed Width calculate record size
            let recordSize = 0;
            if (!delimited) {
                recordSize = defaultFieldsExport.map(x => x.FieldLength).reduce((a, b) => a + b);
            }

            request(
                `/FlowControl/ExportLayoutDefault?listDefault`,
                {
                    credentials: "same-origin",
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json; charset=utf-8",
                    },
                    body: JSON.stringify({
                        IsDelimited: delimited,
                        Delimiter_Size: delimited ? inputLayout?.Delimiter : recordSize,
                        Fields: defaultFieldsExport,
                    }),
                },
                dispatch
            )
                .then(response => response.blob())
                .then(blob => {
                    // Create blob link to download
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const anchorLink = document.createElement("a");
                    anchorLink.href = url;
                    anchorLink.setAttribute("download", `Output Default Layout.csv`);

                    // Append to html anchorLink element page
                    document.body.appendChild(anchorLink);

                    // Start download
                    anchorLink.click();

                    // Clean up and remove the anchorLink
                    anchorLink.parentNode?.removeChild(anchorLink);
                });
        }

        setTimeout(() => {
            setExportLockedOut(false);
        }, 3000);
    };

    const renderLayoutsDropdown = () => {
        const options = layouts
            .filter(x => x.LayoutType == layoutType)
            .map(item => ({
                label: item.LayoutName,
                value: item.LayoutId,
                item,
            }))
            .sort(labelCompare);

        const currentLayout = options.filter(x => x.value == selectedLayout);
        return (
            <FormControl style={{ width: "65%" }}>
                <Autocomplete
                    id="destinationSelectionLabel"
                    options={options}
                    getOptionLabel={option => (option && option.label) || ""}
                    onChange={(_event, selected) => {
                        handleLayoutChanged(_event, selected && selected.value ? selected.value : null);
                    }}
                    autoComplete
                    disabled={editLayoutMode}
                    value={selectedLayout > 0 ? currentLayout[0] : null}
                    renderInput={params => <TextField {...params} fullWidth />}
                />
            </FormControl>
        );
    };

    const renderLayoutExport = () => (
        <Tooltip title="Export Layout">
            <div style={{ margin: "0 10px 0 0" }}>
                <IconButton
                    onClick={() => exportLayout()}
                    aria-label="export layout"
                    disabled={awsIsDownloading || exportLockedOut}
                >
                    <i className="material-icons">vertical_align_bottom</i>
                </IconButton>
            </div>
        </Tooltip>
    );

    return (
        <>
            <div className="flow-edit-body" style={{ marginTop: "20px" }}>
                <Paper style={{ padding: "20px" }}>
                    <FormLabel>
                        {layoutType == LayoutTypes.Input ? (
                            <span>Input Layout</span>
                        ) : (
                            <>
                                <span>Output Layout</span>
                                {isRequired ? null : (
                                    <i>
                                        <b>&nbsp;(Optional)</b>
                                    </i>
                                )}
                            </>
                        )}
                        {isTable ? (
                            <i>
                                <b>&nbsp;(must be comma delimited)</b>
                            </i>
                        ) : null}
                    </FormLabel>
                    <div className="m-t" style={{ display: "flex", justifyContent: "space-between" }}>
                        {renderLayoutsDropdown()}
                        {!editLayoutMode && (
                            <div style={{ display: "flex", justifyContent: "end" }}>
                                {renderLayoutExport()}
                                {layout && (
                                    <Button
                                        id="edit-output-layout"
                                        variant="contained"
                                        color="default"
                                        startIcon={<i className="material-icons">edit</i>}
                                        onClick={() => {
                                            dispatch(setHasUnsavedLayoutChanges());
                                            dispatch(setServiceSelectedLayout(layout.LayoutId));
                                            setEditLayoutMode(true);
                                        }}
                                        disabled={!flowPermissions.canEdit || awsIsDownloading || editLayoutMode}
                                    >
                                        Edit Layout
                                    </Button>
                                )}
                                <Button
                                    id="add-output-layout"
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<i className="material-icons">add</i>}
                                    onClick={() => {
                                        dispatch(setHasUnsavedLayoutChanges());
                                        dispatch(setServiceSelectedLayout(0));
                                        setEditLayoutMode(true);
                                    }}
                                    disabled={!flowPermissions.canEdit || awsIsDownloading || editLayoutMode}
                                >
                                    Add Layout
                                </Button>
                            </div>
                        )}
                    </div>
                    {!editLayoutMode && (
                        <div className="m-t">
                            <LayoutPreview
                                layoutId={selectedLayout}
                                isSnowflakeTable={isRequired}
                                hasColumnOptFeats={hasColumnOptFeats}
                                showColumnOptService={showColumnOptService}
                            />
                        </div>
                    )}
                </Paper>
                <div className="m-t">
                    {editLayoutMode && (
                        <LayoutBuilder
                            layoutType={layoutType}
                            flowExternalServiceId={flowExternalService.FlowExternalServiceId}
                            onCancel={() => {
                                dispatch(setHasUnsavedLayoutChanges(false));
                                setEditLayoutMode(false);
                            }}
                            isTable={isTable}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
export default FlowControlLayoutPicker;
